import type { Canvas } from 'fabric';

// Reference canvas size used to normalise watermark density across
// document sizes — a 200-px design and a 4000-px design end up with
// roughly the same tiled watermark count.
const WATERMARK_REFERENCE_WIDTH = 2000;

type TWatermarkAsset = {
    scale: { x: number; y: number };
    size: { height: number; width: number };
    url: string;
};

type TApplyWatermarkParams = {
    clonedFabricCanvas: Canvas | HTMLCanvasElement;
    opacity?: number;
    scale?: { x: number; y: number };
    watermarkImage: HTMLCanvasElement | HTMLImageElement;
    xOffset?: number;
    yOffset?: number;
};

// Tile a semi-transparent watermark image across the whole canvas.
// Scales the tile size + offsets by canvasWidth / WATERMARK_REFERENCE_WIDTH
// so the tile count is stable across resolutions.
function applyWatermarkToCanvas({
    clonedFabricCanvas,
    opacity = 0.65,
    scale = { x: 2, y: 2 },
    watermarkImage,
    xOffset = 369,
    yOffset = 66,
}: TApplyWatermarkParams): void {
    const ctx = clonedFabricCanvas.getContext('2d');
    if (!ctx) {
        console.error('applyWatermarkToCanvas: 2D context not found');
        return;
    }
    ctx.globalAlpha = opacity;

    const canvasScaleFactor = clonedFabricCanvas.width / WATERMARK_REFERENCE_WIDTH;
    const adjustedScale = { x: scale.x * canvasScaleFactor, y: scale.y * canvasScaleFactor };
    const adjustedXOffset = xOffset * canvasScaleFactor;
    const adjustedYOffset = yOffset * canvasScaleFactor;
    const scaledWidth = watermarkImage.width * adjustedScale.x;
    const scaledHeight = watermarkImage.height * adjustedScale.y;

    let toggle = 0;
    for (let j = -adjustedYOffset; j < clonedFabricCanvas.height * 2; j += adjustedYOffset) {
        const horizontalOffset = toggle % 2 === 0 ? adjustedXOffset : adjustedXOffset * 0.25;
        for (let i = -horizontalOffset; i < clonedFabricCanvas.width * 2; i += adjustedXOffset * 1.5) {
            ctx.drawImage(watermarkImage, i, j, scaledWidth, scaledHeight);
        }
        toggle++;
    }

    ctx.globalAlpha = 1;
}

export { applyWatermarkToCanvas };
export type { TWatermarkAsset };
